import { gql } from '@apollo/client';
import type { RefObject } from 'react';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { ErrorBlock } from '../../components/ErrorBlock';
import { CHILD_LIMIT } from '../../constants/message';
import { useQueryCallback } from '../../hooks/useQueryCallback';
import { useRefLatest } from '../../hooks/useRefLatest';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { readSelectorOnce } from '../../redux/redux.utils';
import type { MessageId } from '../../redux/slices/yakli-slice';
import {
  closeEditorCancelAction,
  startLoadingMessageChildren,
  stopLoadingMessageChildren,
  updateYakliMessage,
} from '../../redux/slices/yakli-slice';
import type { QueryType } from '../../types/api';
import { handleError } from '../../utils/common-utils';
import { consumeInteractionIntent } from '../../utils/interactionIntent';
import type { VoterInstance } from '../BottomActions/VoteBottomAction';
import { useWithoutAnswers } from '../Discussion/WithoutAnswersProvider';
import { ThreadAutoHeight } from '../ThreadAutoHeight';
import { ThreadIsRootProvider } from '../ThreadContexts/ThreadIsRootContext';
import {
  ChildMessageMetaFragment,
  ChildMessageRelevanceFragment,
  generateChildren,
  ThreadMessageFragment,
} from '../ThreadMessages/query';
import type { EditorCloseCallback } from '../Write/ThreadEditorForm';
import { newResponseKeyInBbf } from './thread-service';
import { getStateMsg } from './thread-slice-utils';
import { ThreadMessageBubbleFlowScroll } from './ThreadMessageBubbleFlowScroll';
import { ThreadMessageCard } from './ThreadMessageCard';
import { ThreadMessageNodeRecursiveWrapper } from './ThreadMessageNodeRecursiveWrapper';
import { ThreadMessageOrEditor } from './ThreadMessageOrEditor';

export interface ThreadMessageChildrenInstance {
  closeEditorCancel: EditorCloseCallback;
}

export type ThreadMessageChildrenRef = RefObject<ThreadMessageChildrenInstance>;

export type ThreadMessageNodeProps = React.PropsWithChildren<{
  /** The target message in the yakli URL that will be displayed with a gray background. */
  targetedId?: string;
  /** If true, ThreadInteractiveContent does not truncate the message content by default. */
  unlimited?: boolean;
  /** For the recursion: sends the event to the parent message, to trigger the vote. */
  onSubmitNewAnswer?: EditorCloseCallback;
  isRoot?: boolean;
  rootMessageId?: MessageId;
  parentMessageId: MessageId | undefined; // Only use case: when closing the text editor, it updates the parent's selectedChidId. And the current message was not a real message, so not found in the redux state.
  currentMessageId: MessageId; // TODO
  isYakliDisplay?: boolean;
  className?: string;
}>;

// The work around the list of messages, related to the bubble flow, could be moved to a child component,
// either the bubble flow component or a wrapper. It would make ThreadMessageNode lighter.

export const ThreadMessageNode = memo(
  forwardRef<ThreadMessageChildrenInstance, ThreadMessageNodeProps>(function ThreadMessageNode(
    props,
    ref,
  ) {
    const { currentMessageId } = props;
    const currentMessageIdRef = useRefLatest(currentMessageId);

    const dispatch = useAppDispatch();

    const childMessagesIds = useAppSelector(
      s => getStateMsg(s, currentMessageId, props.isYakliDisplay)?.childMessagesIds,
    );

    if (!currentMessageId) {
      const err = new Error(`No currentMessageId in ThreadMessageNode`);
      handleError(err);
      throw err;
    }

    // If the parent message does not have child messages already loaded, we query them before we continue to render the discussion.
    const messagesQuery = useMemo(
      () => gql`
          query ChildMessagesQuery($messageId: ID) {
              website {
                  id
                  findMessage(messageId: $messageId) {
                    id
                    # ...ThreadMessageFragment
                    ${generateChildren(1, 'relevance')}
                    # childMessages(offset: 0, limit: ${CHILD_LIMIT}, sort: "relevance") {
                    #   ...ChildMessageMetaFragment
                    #   ...ChildMessageRelevanceFragment
                    #   messages {
                    #     id
                    #     ...ThreadMessageFragment
                    #   }
                    # }
                  }
              }
          }
          ${ThreadMessageFragment}
          ${ChildMessageMetaFragment}
          ${ChildMessageRelevanceFragment}
      `,
      [],
    );

    const updateStateStartLoadingchildren = useCallback(() => {
      dispatch(startLoadingMessageChildren(currentMessageIdRef.current));
    }, [currentMessageIdRef, dispatch]);

    const addMessageToCache = useCallback(
      (data: QueryType) => {
        const message = data.website?.findMessage;
        dispatch(updateYakliMessage({ message, updateChildren: true }));
      },
      [dispatch],
    );

    const handleLoadChildrenError = useCallback(
      (error: any) => {
        dispatch(stopLoadingMessageChildren(currentMessageIdRef.current));
        handleError(error);
      },
      [currentMessageIdRef, dispatch],
    );

    const { error, refetch } = useQueryCallback({
      query: messagesQuery,
      variables: { messageId: currentMessageId },
      skipInitialFetch: true,
      onStartLoading: updateStateStartLoadingchildren,
      onResponse: addMessageToCache,
      onError: handleLoadChildrenError,
    });

    // Check if at least one of the children is missing in the cache. If yes, refetch children.
    useEffect(() => {
      const hasMissingChild = readSelectorOnce(s => {
        if (currentMessageId === newResponseKeyInBbf) return false;
        const currentMessage = getStateMsg(s, currentMessageId, props.isYakliDisplay);
        if (currentMessage?.childMessagesIds == null) {
          return true;
        }
        for (const childId of currentMessage?.childMessagesIds || []) {
          if (!getStateMsg(s, childId, props.isYakliDisplay)) {
            return true;
          }
        }
        return false;
      });
      if (hasMissingChild) {
        // Ce refetch sert le MONTAGE (cascade d'ouverture yakli, profondeur
        // au-delà du payload SSR) ET le GESTE (clic/scroll BubbleFlow, qui
        // vient de signaler son intention). Seul le geste est facturé comme
        // Interaction (B10_05) : l'intention, consommée UNE fois, marque au
        // plus un fetch par clic — la cascade qui suit reste non marquée.
        // ⚠ Ne JAMAIS poser `gcInteraction` en dur dans les args du hook.
        refetch(
          consumeInteractionIntent('bbf-child') ? { context: { gcInteraction: true } } : undefined,
        );
      }
    }, [childMessagesIds, currentMessageId, props.isYakliDisplay, refetch]);

    if (error) {
      return <ErrorBlock error={error} />;
    }

    return <ThreadMessageNode2 ref={ref} {...props} />;
  }),
);

interface ThreadMessageNodeProps2 extends ThreadMessageNodeProps {}

export const ThreadMessageNode2 = memo(
  forwardRef<ThreadMessageChildrenInstance, ThreadMessageNodeProps2>(
    (
      {
        targetedId,
        unlimited,
        onSubmitNewAnswer,
        isRoot,
        rootMessageId,
        parentMessageId,
        currentMessageId,
        isYakliDisplay,
        className,
        children,
      },
      ref,
    ) => {
      const dispatch = useAppDispatch();
      const parentMessageIdRef = useRefLatest(parentMessageId);

      const withoutAnswers = useWithoutAnswers();

      const rootMessage = useAppSelector(s =>
        rootMessageId ? getStateMsg(s, rootMessageId, isYakliDisplay) : undefined,
      );

      if (isRoot && !rootMessage) {
        throw new Error(
          `ThreadMessageNode isRoot but rootMessage is missing. rootMessageId: ${rootMessageId}`,
        );
      }

      // const currentMessage = useAppSelector(s => getStateMsg(s, currentMessageId));
      const currentMessageAuthor = useAppSelector(
        s => getStateMsg(s, currentMessageId, isYakliDisplay)?.author,
      );
      const currentMessageAuthorRef = useRefLatest(currentMessageAuthor);

      const currentOpenIsNewResponse = !isRoot && currentMessageId === newResponseKeyInBbf;

      const closeEditorCancel = useCallback(() => {
        dispatch(
          closeEditorCancelAction({
            parentMessageId: parentMessageIdRef.current,
          }),
        );
      }, [dispatch, parentMessageIdRef]);

      useImperativeHandle(
        ref,
        () => ({
          closeEditorCancel, // Used in ThreadFooterEditor (to challenge - the mobile footer is supposed to have its own instances of editor)
        }),
        [closeEditorCancel],
      );

      // Interactions with the child ThreadMessageNode (open editor & co)

      const voteRef = useRef<VoterInstance>(null);
      const nestedAnswersRef = useRef<ThreadMessageChildrenInstance>(null);

      const handleChildSubmitAnswer = useCallback<EditorCloseCallback>(() => {
        const { voteOnAnswer } = voteRef?.current ?? {};
        if (voteOnAnswer) voteOnAnswer(currentMessageAuthorRef.current);
      }, [currentMessageAuthorRef]);

      // /end Interactions with the child ThreadMessageNode (open editor & co)

      return (
        <>
          <ThreadIsRootProvider condition={isRoot}>
            <ThreadMessageCard
              messageId={currentMessageId}
              voteRef={voteRef}
              nestedAnswersRef={nestedAnswersRef}
              isYakliDisplay={isYakliDisplay}
              className={twMerge('ThreadMessageNode2', className)}
            >
              <ThreadAutoHeight>
                <ThreadMessageOrEditor
                  currentMessageId={currentMessageId}
                  parentMessageId={parentMessageId}
                  onSubmitNewAnswer={onSubmitNewAnswer}
                  onCloseEditor={closeEditorCancel}
                  targetedId={targetedId}
                  unlimited={unlimited}
                  voteRef={voteRef}
                  isYakliDisplay={isYakliDisplay}
                />
                {!withoutAnswers && (
                  <ThreadMessageBubbleFlowScroll parentMessageId={currentMessageId} />
                )}
              </ThreadAutoHeight>
            </ThreadMessageCard>
          </ThreadIsRootProvider>

          {!withoutAnswers && !currentOpenIsNewResponse && !!currentMessageId && (
            <>
              <ThreadMessageNodeRecursiveWrapper
                messageId={currentMessageId}
                nestedAnswersRef={nestedAnswersRef}
                targetedId={targetedId}
                unlimited={unlimited}
                onSubmitNewAnswer={handleChildSubmitAnswer}
                isYakliDisplay={isYakliDisplay}
              />
            </>
          )}

          {children}
        </>
      );
    },
  ),
);
